import React, { useState } from 'react';

import { format, parseISO } from 'date-fns';

import BulkUploadBlock from './bulk/BulkUploadBlock';
import GhostButton from './GhostButton';
import AddSessionForm from './sessions/AddSessionForm';
import SessionTypeList from './sessions/SessionTypeList';
import SetupCard from './SetupCard';
import StepShell from './StepShell';
import useStepPayload from './useStepPayload';

import { useToast } from 'Hooks/useToast';

const subhead = 'tw-mt-5 tw-mb-2.5 tw-text-0.5sm tw-font-semibold tw-text-charcoal-50';

function sessionLine(session) {
    const date = format(parseISO(session.date), 'd MMM yyyy');
    const times = session.start_time && session.end_time ? `${session.start_time}–${session.end_time}` : '';

    return [session.name, date, times, session.venue_name].filter(Boolean).join(' · ');
}

/**
 * Sessions: session types first (the starter set is offered when the page
 * opens), then sessions grouped by event, then the sessions template. Adding
 * or uploading sessions waits until types, events and venues all exist.
 */
export default function SessionsStep({ step, urls, deferring, saving, onDefer, onNext, onStatusChange, onGoTo }) {
    const { data, send, load } = useStepPayload(urls.data, onStatusChange, () => axios.post(urls.starter));
    const [adding, setAdding] = useState(false);
    const toast = useToast();

    const lede = $t('Create your session types first, then schedule sessions against your events within the event window set in Key dates.');

    const write = (request) =>
        send(request).catch((e) => {
            if (e?.response?.status !== 422) {
                toast.error(e?.response?.data?.message || $t('Something went wrong. Please try again.'));
                load();
            }

            throw e;
        });

    const typeUrl = (type) => urls.type_template.replace('TYPE_ID', type.id);

    const addSession = (body) =>
        write(() => axios.post(urls.items, body)).then((response) => {
            setAdding(false);

            if (response.pending_approval) {
                toast.success(response.message);
            }
        });

    const imported = (response) => {
        send(
            () => Promise.resolve({ data: response }),
            (body) => body.step,
        ).catch(() => load());
        toast.success(response.result.message);
    };

    if (!data) {
        return (
            <StepShell step={step} lede={lede} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    const { blockers } = data;
    const blocked = blockers.needs_types || blockers.needs_events || blockers.needs_venues;
    const withSessions = data.events.filter((event) => event.sessions_count > 0);

    return (
        <StepShell step={step} lede={lede} deferring={deferring} saving={saving} onDefer={onDefer} onNext={onNext}>
            <h3 className={subhead}>{$t('Session types')}</h3>
            <SessionTypeList
                types={data.session_types}
                onAdd={(body) => write(() => axios.post(urls.types, body))}
                onUpdate={(type, body) => write(() => axios.put(typeUrl(type), body))}
                onDelete={(type) => write(() => axios.delete(typeUrl(type)))}
            />

            <h3 className={subhead}>{$t('Sessions')}</h3>
            <div className="tw-flex tw-flex-col tw-gap-y-2 tw-max-w-2xl">
                {blocked && (
                    <p className="tw-mb-0 tw-text-sm tw-text-charcoal-75">
                        {blockers.needs_types && $t('Add a session type above before adding sessions.')}
                        {!blockers.needs_types && blockers.needs_events && (
                            <>
                                {$t('Sessions are scheduled against events.')}{' '}
                                <button
                                    type="button"
                                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-blue hover:tw-underline"
                                    onClick={() => onGoTo('events')}
                                >
                                    {$t('Add events on the Events step')}
                                </button>
                            </>
                        )}
                        {!blockers.needs_types && !blockers.needs_events && blockers.needs_venues && (
                            <>
                                {$t('Sessions take place at venues.')}{' '}
                                <button
                                    type="button"
                                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-blue hover:tw-underline"
                                    onClick={() => onGoTo('venues')}
                                >
                                    {$t('Add venues on the Venues step')}
                                </button>
                            </>
                        )}
                    </p>
                )}

                {withSessions.map((event) => (
                    <SetupCard
                        key={event.id}
                        heading={event.name}
                        chip={$t('Event')}
                        lines={[
                            ...event.sessions.map(sessionLine),
                            ...(event.sessions_count > event.sessions.length
                                ? [$t('and {{count}} more', { count: event.sessions_count - event.sessions.length })]
                                : []),
                        ]}
                    />
                ))}

                {data.can_create && !blocked && adding && (
                    <AddSessionForm
                        types={data.session_types}
                        events={data.events}
                        venues={data.venues}
                        onSubmit={addSession}
                        onCancel={() => setAdding(false)}
                        onGoToVenues={() => onGoTo('venues')}
                    />
                )}

                {data.can_create && !blocked && (
                    <div className="tw-mt-1">
                        <GhostButton expanded={adding} onClick={() => setAdding((current) => !current)}>
                            {$t('+ Add a session to an event')}
                        </GhostButton>
                    </div>
                )}
            </div>

            {!blocked && (
                <BulkUploadBlock
                    id="setup-wizard-sessions-bulk"
                    templateUrl={urls.template}
                    templateLabel={$t('Download the sessions template')}
                    importUrl={urls.import}
                    canImport={data.can_import}
                    onImported={imported}
                />
            )}
        </StepShell>
    );
}
